import type { FlatWbsRow, WbsTreeNode } from './types';
import { getDateRange, dateToX, DEPTH_ROW_BG, DEPTH_ROW_HOVER } from './wbsUtils';
import WbsGanttHeader from './WbsGanttHeader';
import WbsGanttBar from './WbsGanttBar';

interface Props {
  wbsData: WbsTreeNode[];
  flattenedRows: FlatWbsRow[];
  timeUnit: 'day' | 'week' | 'month';
  scrollRef: React.RefObject<HTMLDivElement>;
  hoveredRowId: string | null;
  onHoverRow: (id: string | null) => void;
  onFieldUpdate: (wbsItemId: string, field: string, value: unknown) => void;
}

const DAY_WIDTH: Record<string, number> = {
  day: 32,
  week: 12,
  month: 4,
};

const ROW_HEIGHT = 33; // border-b 포함 행 높이

export default function WbsGanttChart({ wbsData, flattenedRows, timeUnit, scrollRef, hoveredRowId, onHoverRow, onFieldUpdate }: Props) {
  const range = getDateRange(wbsData);
  const dayWidth = DAY_WIDTH[timeUnit];

  if (!range) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
        날짜를 설정하면 Gantt 차트가 표시됩니다.
      </div>
    );
  }

  const { minDate, maxDate } = range;
  const totalDays = Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
  const totalWidth = totalDays * dayWidth;

  // 오늘 선
  const today = new Date();
  const todayX = dateToX(today, minDate, dayWidth);
  const todayVisible = today >= minDate && today <= maxDate;

  return (
    <div className="flex-1 overflow-x-auto overflow-y-hidden" ref={scrollRef}>
      <div style={{ width: totalWidth, minWidth: '100%' }}>
        {/* 시간축 헤더 */}
        <WbsGanttHeader
          minDate={minDate}
          maxDate={maxDate}
          dayWidth={dayWidth}
          totalWidth={totalWidth}
          timeUnit={timeUnit}
        />

        {/* Gantt 바 영역 */}
        <div className="relative">
          {/* 행별 바 */}
          {flattenedRows.map((row) => {
            const isHovered = hoveredRowId === row.node.wbsItemId;
            const rowBg = isHovered
              ? (DEPTH_ROW_HOVER[row.node.depth] || '')
              : (DEPTH_ROW_BG[row.node.depth] || '');
            return (
              <div
                key={row.node.wbsItemId}
                className={`relative border-b border-gray-200 ${rowBg}`}
                style={{ height: ROW_HEIGHT }}
                onMouseEnter={() => onHoverRow(row.node.wbsItemId)}
                onMouseLeave={() => onHoverRow(null)}
              >
                <WbsGanttBar
                  node={row.node}
                  minDate={minDate}
                  dayWidth={dayWidth}
                  rowHeight={ROW_HEIGHT}
                  onDateChange={(wbsItemId, field, value) => onFieldUpdate(wbsItemId, field, value)}
                />
              </div>
            );
          })}

          {/* 그리드 오버레이: 세로 경계선 + 주말 빗살무늬 (행 위에 렌더링, 클릭 투과) */}
          <div className="absolute inset-0 pointer-events-none z-[1]">
            {renderDayGrid(minDate, totalDays, dayWidth)}
          </div>

          {/* 오늘 선 */}
          {todayVisible && (
            <div
              className="absolute top-0 bottom-0 w-px bg-red-400 z-10 pointer-events-none"
              style={{ left: todayX + dayWidth / 2 }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** 세로 그리드 선 + 주말 빗살무늬 음영 */
function renderDayGrid(minDate: Date, totalDays: number, dayWidth: number) {
  const columns: JSX.Element[] = [];
  const current = new Date(minDate);
  for (let i = 0; i < totalDays; i++) {
    const dow = current.getDay();
    const isWeekend = dow === 0 || dow === 6;
    columns.push(
      <div
        key={i}
        className="absolute top-0 bottom-0 border-r border-gray-100"
        style={{
          left: i * dayWidth,
          width: dayWidth,
          ...(isWeekend
            ? {
                background:
                  'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(0,0,0,0.07) 3px, rgba(0,0,0,0.07) 4px)',
              }
            : {}),
        }}
      />,
    );
    current.setDate(current.getDate() + 1);
  }
  return <>{columns}</>;
}
